/**
 * Pipeline DAG View
 *
 * Displays a pipeline's dependency graph with interactive node selection.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { DAGGraph, type DAGNode, type DAGEdge } from '@brutal/brutal/organisms';
import { useAgent } from '../hooks/useAgent';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface Pipeline {
  id: string;
  name: string;
  environment: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  nodes: DAGNode[];
  edges: DAGEdge[];
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

function getStatusColor(status: Pipeline['status']): string {
  switch (status) {
    case 'completed':
      return '#00ff88';
    case 'running':
      return '#ffaa00';
    case 'pending':
      return '#666';
    case 'failed':
      return '#ff4444';
  }
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function PipelineDAG() {
  const { id } = useParams<{ id: string }>();
  const { isConnected } = useAgent();
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch pipeline data
  useEffect(() => {
    if (!isConnected || !id) {
      setLoading(!isConnected);
      return;
    }

    const fetchPipeline = async () => {
      setLoading(true);
      setError(null);
      try {
        const { getRpcClient } = await import('../lib/rpc-client');
        const client = getRpcClient();
        const result: unknown = await client.call('pipeline.get', { id });

        if (
          result !== null &&
          typeof result === 'object' &&
          'pipeline' in result &&
          (result as Record<string, unknown>).pipeline !== null
        ) {
          setPipeline((result as Record<string, unknown>).pipeline as Pipeline);
        } else {
          // pipeline.get doesn't have DAG data yet — show message instead of mock data
          setError('Pipeline DAG visualization not yet available for this execution');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch pipeline');
      } finally {
        setLoading(false);
      }
    };

    fetchPipeline();
  }, [isConnected, id]);

  const handleNodeClick = useCallback(
    (nodeId: string) => {
      const node = pipeline?.nodes.find((n) => n.id === nodeId);
      if (node) {
        // Navigate to deployment detail when available
        console.log('Node clicked:', node);
      }
    },
    [pipeline],
  );

  if (!isConnected) {
    return (
      <div>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>Pipeline</h2>
        <p style={{ color: '#888' }}>Connecting to agent...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>Pipeline</h2>
        <p style={{ color: '#888' }}>Loading...</p>
      </div>
    );
  }

  if (error || !pipeline) {
    return (
      <div>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>Pipeline</h2>
        <p style={{ color: '#ff4444' }}>Error: {error ?? 'Pipeline not found'}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '2rem', margin: 0 }}>{pipeline.name}</h2>
          <p style={{ color: '#888', margin: '4px 0 0 0' }}>
            {pipeline.environment} {'\u2022'} {pipeline.id}
          </p>
        </div>
        <span
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            backgroundColor: `${getStatusColor(pipeline.status)}20`,
            color: getStatusColor(pipeline.status),
            fontWeight: 'bold',
          }}
        >
          {pipeline.status}
        </span>
      </div>

      {/* DAG Graph */}
      <div
        style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
        }}
      >
        <DAGGraph
          nodes={pipeline.nodes}
          edges={pipeline.edges}
          onNodeClick={handleNodeClick}
          width={800}
          height={400}
        />
      </div>

      {/* Node List */}
      <div style={{ backgroundColor: '#1a1a1a', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #333' }}>
              <th
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  color: '#888',
                  fontWeight: 'normal',
                }}
              >
                Artifact
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  color: '#888',
                  fontWeight: 'normal',
                }}
              >
                Version
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  color: '#888',
                  fontWeight: 'normal',
                }}
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {pipeline.nodes.map((node) => {
              const nodeStatusColor =
                node.status === 'succeeded'
                  ? '#00ff88'
                  : node.status === 'running'
                    ? '#ffaa00'
                    : node.status === 'failed'
                      ? '#ff4444'
                      : node.status === 'skipped'
                        ? '#444'
                        : '#666';
              return (
                <tr
                  key={node.id}
                  style={{ borderBottom: '1px solid #222', cursor: 'pointer' }}
                  onClick={() => handleNodeClick(node.id)}
                >
                  <td style={{ padding: '12px 16px' }}>{node.artifact}</td>
                  <td style={{ padding: '12px 16px', color: '#888' }}>{node.version}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        backgroundColor: `${nodeStatusColor}20`,
                        color: nodeStatusColor,
                        fontWeight: 'bold',
                      }}
                    >
                      {node.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
